import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getRoles, getPermissions, createRole, updateRole, deleteRole } from '../../services/api';

interface Role {
    id: string;
    name: string;
    description: string;
    permissions: string[];
}

interface Permission {
    id: string;
    name: string;
    description: string;
}

const RoleManagement: React.FC = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Add role modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [newRole, setNewRole] = useState<{ name: string; description: string; permissions: string[] }>({
        name: '',
        description: '',
        permissions: []
    });
    
    // Edit role modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editRole, setEditRole] = useState<Role | null>(null);

    useEffect(() => {
        fetchRoles();
        fetchPermissions();
    }, []);

    const fetchRoles = async () => {
        try {
            const data = await getRoles();
            setRoles(data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching roles:', err);
            toast.error('Failed to fetch roles');
            setError('Failed to fetch roles');
            setLoading(false);
        }
    };

    const fetchPermissions = async () => {
        try {
            const data = await getPermissions();
            setPermissions(data);
        } catch (err) {
            console.error('Error fetching permissions:', err);
            toast.error('Failed to fetch permissions');
        }
    };

    const handleDelete = async (roleId: string) => {
        if (window.confirm('Are you sure you want to delete this role?')) {
            try {
                await deleteRole(roleId);
                setRoles(roles.filter(role => role.id !== roleId));
                toast.success('Role deleted successfully');
            } catch (err) {
                console.error('Error deleting role:', err);
                toast.error('Failed to delete role');
            }
        }
    };

    const handleAddRole = async () => {
        try {
            // Validate role data
            if (!newRole.name.trim()) {
                toast.error('Role name is required');
                return;
            }

            if (newRole.permissions.length === 0) {
                toast.error('At least one permission must be selected');
                return;
            }

            const data = await createRole(newRole);
            setRoles([...roles, data]);
            setShowAddModal(false);
            resetNewRole();
            toast.success('Role created successfully');
        } catch (err) {
            console.error('Error creating role:', err);
            toast.error('Failed to create role');
        }
    };

    const handleOpenEditModal = (role: Role) => {
        setEditRole({ ...role });
        setShowEditModal(true);
    };

    const handleEditRole = async () => {
        if (!editRole) return;

        try {
            // Validate role data
            if (!editRole.name.trim()) {
                toast.error('Role name is required');
                return;
            }

            if (editRole.permissions.length === 0) {
                toast.error('At least one permission must be selected');
                return;
            }

            const data = await updateRole(editRole.id, {
                name: editRole.name,
                description: editRole.description,
                permissions: editRole.permissions
            });

            // Update the roles list with the edited role
            setRoles(roles.map(role => role.id === editRole.id ? data : role));
            setShowEditModal(false);
            setEditRole(null);
            toast.success('Role updated successfully');
        } catch (err) {
            console.error('Error updating role:', err);
            toast.error('Failed to update role');
        }
    };

    const resetNewRole = () => {
        setNewRole({
            name: '',
            description: '',
            permissions: []
        });
    };

    const togglePermission = (permissionId: string, isEdit = false) => {
        if (isEdit && editRole) {
            if (editRole.permissions.includes(permissionId)) {
                setEditRole({
                    ...editRole,
                    permissions: editRole.permissions.filter(id => id !== permissionId)
                });
            } else {
                setEditRole({
                    ...editRole,
                    permissions: [...editRole.permissions, permissionId]
                });
            }
        } else {
            if (newRole.permissions.includes(permissionId)) {
                setNewRole({
                    ...newRole,
                    permissions: newRole.permissions.filter(id => id !== permissionId)
                });
            } else {
                setNewRole({
                    ...newRole,
                    permissions: [...newRole.permissions, permissionId]
                });
            }
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    if (error) {
        return <div className="text-red-500 text-center p-4">{error}</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Role Management</h1>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add New Role
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Role Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Permissions
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {roles.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                                    No roles found. Add your first role to get started.
                                </td>
                            </tr>
                        ) : (
                            roles.map((role) => (
                                <tr key={role.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{role.name}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-500">{role.description}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-2">
                                            {role.permissions.map((permission) => {
                                                // Find the permission name if available
                                                const permObject = permissions.find(p => p.id === permission);
                                                const displayName = permObject ? permObject.name : permission;
                                                
                                                return (
                                                    <span
                                                        key={permission}
                                                        className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                                                        title={permObject?.description}
                                                    >
                                                        {displayName}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleOpenEditModal(role)}
                                            className="text-blue-600 hover:text-blue-900 mr-4"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(role.id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add Role Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Add New Role</h2>
                            <button 
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetNewRole();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Role Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newRole.name}
                                    onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter role name"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={newRole.description}
                                    onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter role description"
                                    rows={3}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Permissions <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-300 rounded-md">
                                    {permissions.map((permission) => (
                                        <div key={permission.id} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`perm-${permission.id}`}
                                                checked={newRole.permissions.includes(permission.id)}
                                                onChange={() => togglePermission(permission.id)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor={`perm-${permission.id}`} className="ml-2 text-sm text-gray-700">
                                                {permission.name}
                                                <span className="block text-xs text-gray-500">{permission.description}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={() => {
                                        setShowAddModal(false);
                                        resetNewRole();
                                    }}
                                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md mr-2 hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddRole}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                >
                                    Create Role
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Role Modal */}
            {showEditModal && editRole && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Edit Role</h2>
                            <button 
                                onClick={() => {
                                    setShowEditModal(false);
                                    setEditRole(null);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Role Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={editRole.name}
                                    onChange={(e) => setEditRole({...editRole, name: e.target.value})}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter role name"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={editRole.description}
                                    onChange={(e) => setEditRole({...editRole, description: e.target.value})}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter role description"
                                    rows={3}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Permissions <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-300 rounded-md">
                                    {permissions.map((permission) => (
                                        <div key={permission.id} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`edit-perm-${permission.id}`}
                                                checked={editRole.permissions.includes(permission.id)}
                                                onChange={() => togglePermission(permission.id, true)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor={`edit-perm-${permission.id}`} className="ml-2 text-sm text-gray-700">
                                                {permission.name}
                                                <span className="block text-xs text-gray-500">{permission.description}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditRole(null);
                                    }}
                                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md mr-2 hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEditRole}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                >
                                    Update Role
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleManagement; 